import { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import replace from 'lodash/replace';
import S3Uploader from '@base/S3Uploader/S3Uploader';
import { fetchS3SignInUrl } from '@actions/others';
import { addClassDpp } from '@api/fetchers/class/updaters';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import { getImage } from '@utils/imageHelper';
import { Fonts, Spacings } from '@styles/index';
import {
  EDUCATOR_COURSE_PAGE,
  EDUCATOR_PRACTICE_DPP_CLICKED,
  EDUCATOR_PRACTICE_DPP_UPLOADED,
  EDUCATOR_PRACTICE_UPLOAD_DPP_CLICKED
} from '@constants/segment';
import useDPPEvents from '../AutoDPP/useDPPEvents';

const getClassDpp = (lesson) => (lesson?.dpp === undefined ? null : lesson.dpp);

const UploadWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: ${Spacings.SPACING_10B};
`;

const UploadLabel = styled.label`
  display: flex;
  align-items: center;
  height: ${Spacings.SPACING_10B};
  margin: 0;
  cursor: pointer;
`;

const HiddenUploader = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
`;

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacings.SPACING_1B};
  box-sizing: border-box;
  height: ${Spacings.SPACING_10B};
  min-height: ${Spacings.SPACING_10B};
  padding: 0 ${Spacings.SPACING_4B};
  background-color: var(--color-base-1);
  color: var(--color-progress-base-primary);
  border: 1px solid var(--color-i-divider);
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.SEMIBOLD};
  cursor: pointer;
  text-transform: none;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1;
  text-decoration: none;
`;

const UploadIcon = styled.img`
  width: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
  flex-shrink: 0;
`;

const FileName = styled.span`
  display: inline-block;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EducatorClassDpp = ({ lesson }) => {
  const dispatch = useDispatchToStore();
  const { open: showSnackbar } = useSnackbar();
  const trackEventsOnSegment = useDPPEvents();
  const pendingFileName = useRef('');
  const [localDpp, setLocalDpp] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const dpp = localDpp || getClassDpp(lesson);
  const canView = Boolean(dpp?.url);
  const canUpload = dpp != null && !canView;

  const getEventProps = (extra = {}) => ({
    last_primary_source: EDUCATOR_COURSE_PAGE,
    goal_name: lesson?.goalName,
    goal_uid: lesson?.goalUid ?? 'NA',
    lesson_uid: lesson?.uid ?? 'NA',
    lesson_title: lesson?.name ?? 'NA',
    ...extra
  });

  const getSignedUrl = useCallback(
    (fileData, callback) => {
      const fileName = replace(fileData.name, new RegExp('&', 'g'), 'and');
      pendingFileName.current = fileData.name;
      dispatch(fetchS3SignInUrl(fileName, 1, '', 'application/pdf')).then(
        (res) => {
          if (res.error) {
            showSnackbar({
              message: res.error.message || 'Could not start upload',
              type: 'error'
            });
            setIsUploading(false);
            return;
          }
          callback({
            signedUrl: res.result?.signed_url,
            publicUrl: res.result?.public_url
          });
        }
      );
    },
    [dispatch, showSnackbar]
  );

  const onUploadStart = (data, next) => {
    pendingFileName.current = data.name;
    setIsUploading(true);
    next(data);
  };

  const onUploadError = () => {
    setIsUploading(false);
    showSnackbar({
      message: 'The file could not be uploaded. Please try again.',
      type: 'error'
    });
  };

  const onUploadFinish = async (urlDetails) => {
    const name = pendingFileName.current || 'DPP.pdf';
    const url = urlDetails?.publicUrl;
    if (!url) {
      setIsUploading(false);
      showSnackbar({
        message: 'The file could not be uploaded. Please try again.',
        type: 'error'
      });
      return;
    }
    const response = await addClassDpp({ classUid: lesson?.uid, url, name });
    if (response?.error) {
      setIsUploading(false);
      showSnackbar({
        message:
          response.error?.message || 'Could not save DPP. Please try again.',
        type: 'error'
      });
      return;
    }
    const saved = response?.dpp || response?.result || {};
    setLocalDpp({
      cta: saved?.cta,
      uid: saved?.uid,
      name: saved?.name || name,
      url: saved?.url || url,
      created_at:
        saved?.createdAt || saved?.created_at || new Date().toISOString()
    });
    trackEventsOnSegment(
      EDUCATOR_PRACTICE_DPP_UPLOADED,
      getEventProps({ dpp_uid: saved?.uid || 'NA' })
    );
    setIsUploading(false);
  };

  const stopCardClick = (event) => {
    event.stopPropagation();
  };

  const onUploadButtonClick = (event) => {
    event.stopPropagation();
    trackEventsOnSegment(EDUCATOR_PRACTICE_UPLOAD_DPP_CLICKED, getEventProps());
  };

  const openDpp = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const url = dpp?.url;
    if (!url) return;

    trackEventsOnSegment(
      EDUCATOR_PRACTICE_DPP_CLICKED,
      getEventProps({ dpp_uid: dpp?.uid || 'NA' })
    );

    const previewTab = window.open('', '_blank');
    if (previewTab) previewTab.opener = null;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Could not load DPP');
        return response.blob();
      })
      .then((blob) => {
        const pdfBlob =
          blob.type === 'application/pdf'
            ? blob
            : new Blob([blob], { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(pdfBlob);
        if (previewTab && !previewTab.closed) {
          previewTab.location.href = objectUrl;
          return;
        }
        window.open(objectUrl, '_blank');
      })
      .catch(() => {
        if (previewTab && !previewTab.closed) {
          previewTab.location.href = url;
          return;
        }
        window.open(url, '_blank');
      });
  };

  if (!canUpload && !canView) return null;

  if (canView) {
    return (
      <Action
        as="a"
        href={dpp?.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={openDpp}
        title={dpp?.name}
      >
        <FileName>{dpp?.name}</FileName>
      </Action>
    );
  }

  return (
    <UploadWrap
      onClick={stopCardClick}
      onKeyDown={stopCardClick}
      role="presentation"
    >
      <UploadLabel
        htmlFor={`class-dpp-upload-${lesson?.uid}`}
        onClick={onUploadButtonClick}
      >
        <Action as="span">
          <UploadIcon src={getImage('upload.svg')} alt="" />
          {isUploading ? 'Uploading...' : 'Upload DPP'}
        </Action>
      </UploadLabel>
      <HiddenUploader>
        <S3Uploader
          id={`class-dpp-upload-${lesson?.uid}`}
          getSignedUrl={getSignedUrl}
          onUploadStart={onUploadStart}
          onUploadError={onUploadError}
          onUploadFinish={onUploadFinish}
          acceptedFiles="pdf"
          multiple={false}
          contentDisposition="inline"
        />
      </HiddenUploader>
    </UploadWrap>
  );
};

EducatorClassDpp.propTypes = {
  lesson: PropTypes.objectOf(PropTypes.any).isRequired
};

export default EducatorClassDpp;
